import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Save, Briefcase, CheckCircle, Plus } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * CarrierAffiliations — multi-select form for an advisor's authorized insurance carriers.
 * Used by both Admin (editing any agent) and Agent Portal Settings (self-edit).
 */
const CarrierAffiliations = ({ agentId, embedded = false }) => {
  const [carriers, setCarriers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [agentName, setAgentName] = useState('');
  const [newCarrier, setNewCarrier] = useState('');
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [carriersRes, affRes] = await Promise.all([
          fetch(`${API_URL}/api/compliance/reference/carriers`),
          fetch(`${API_URL}/api/compliance/agents/${agentId}/carrier-affiliations`),
        ]);
        if (carriersRes.ok) setCarriers(await carriersRes.json());
        if (affRes.ok) {
          const aff = await affRes.json();
          setSelected(aff.insurance_carriers_authorized || []);
          setAgentName(aff.agent_name || '');
        }
      } catch (e) {
        console.error('Failed to load carrier affiliations', e);
      } finally {
        setLoading(false);
      }
    };
    if (agentId) load();
  }, [agentId]);

  const toggle = (carrier) => {
    setSelected(prev => prev.includes(carrier) ? prev.filter(c => c !== carrier) : [...prev, carrier]);
  };

  const selectAll = () => setSelected(carriers.slice());
  const clearAll = () => setSelected([]);

  // Iter 65 — add a brand-new carrier to the master list; auto-select it for this advisor.
  const addCarrier = async () => {
    const name = newCarrier.trim();
    if (!name) return;
    setAdding(true);
    try {
      const res = await fetch(`${API_URL}/api/compliance/reference/carriers`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Failed to add carrier'); }
      const updated = await res.json();
      setCarriers(updated);
      setSelected(prev => prev.includes(name) ? prev : [...prev, name]);
      setNewCarrier('');
    } catch (e) {
      alert(e.message);
    } finally {
      setAdding(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(
        `${API_URL}/api/compliance/agents/${agentId}/carrier-affiliations`,
        { method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ insurance_carriers_authorized: selected }) }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Save failed');
      }
      setSavedAt(new Date());
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center py-6 text-gray-500">Loading carrier list...</div>;
  }

  const content = (
    <>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <p className="text-sm text-gray-600">
          Select all insurance carriers you currently hold contracts with (master list of carriers contracted with Utmost Financial Inc.).
          These will auto-populate on the Advisor Disclosure for every new case.
        </p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={selectAll} data-testid="aff-select-all">Select All</Button>
          <Button variant="outline" size="sm" onClick={clearAll} data-testid="aff-clear-all">Clear All</Button>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-2" data-testid="carrier-affiliations-grid">
        {carriers.map(c => {
          const isSel = selected.includes(c);
          return (
            <label
              key={c}
              className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition ${
                isSel ? 'bg-blue-50 border-[#0077B6]' : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <input
                type="checkbox"
                checked={isSel}
                onChange={() => toggle(c)}
                data-testid={`aff-${c.replace(/\s+/g, '-').replace(/[()]/g, '')}`}
              />
              <span className="text-sm">{c}</span>
            </label>
          );
        })}
      </div>

      {/* Iter 65 — add a new carrier that isn't in the master list yet */}
      <div className="flex items-center gap-2 pt-4 mt-4 border-t">
        <input
          type="text"
          value={newCarrier}
          onChange={(e) => setNewCarrier(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCarrier(); } }}
          placeholder="Add a new carrier (e.g., Wawanesa Life)…"
          className="flex-1 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#0077B6]"
          data-testid="add-carrier-input"
        />
        <Button variant="outline" onClick={addCarrier} disabled={adding || !newCarrier.trim()} data-testid="add-carrier-btn">
          <Plus className="h-4 w-4 mr-1" />{adding ? 'Adding…' : 'Add Carrier'}
        </Button>
      </div>
      <p className="text-xs text-gray-400 mt-1">New carriers are added to the shared master list for everyone.</p>

      <div className="flex items-center justify-between pt-4 mt-4 border-t">
        <div className="text-sm text-gray-600">
          <b>{selected.length}</b> of {carriers.length} carriers selected
          {savedAt && (
            <span className="ml-3 text-green-600 inline-flex items-center gap-1">
              <CheckCircle className="h-4 w-4" />Saved {savedAt.toLocaleTimeString()}
            </span>
          )}
        </div>
        <Button onClick={save} disabled={saving} className="bg-[#0077B6]" data-testid="save-affiliations-btn">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save Affiliations'}
        </Button>
      </div>
    </>
  );

  if (embedded) return <div className="space-y-2">{content}</div>;

  return (
    <Card data-testid="carrier-affiliations-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Briefcase className="h-5 w-5" />
          Carrier Affiliations {agentName && <span className="text-sm font-normal text-gray-500">— {agentName}</span>}
        </CardTitle>
      </CardHeader>
      <CardContent>{content}</CardContent>
    </Card>
  );
};

export default CarrierAffiliations;
